<template>
  <div class="wg-card" id="wg-gsc">
    <div class="wg-gsc-head">
      <div :class="isConnected || isExpired ? 'wg-gico-blue' : 'wg-gico'">G</div>
      <div class="txt">
        <div class="t">Google Search Console</div>
        <div class="s">
          <template v-if="isExpired">Connected as {{ accountName }}, access expired</template>
          <template v-else-if="isConnected">Connected as {{ accountLabel }}</template>
          <template v-else-if="availableAccountEmail">
            Google account connected ({{ availableAccountEmail }}). Pick your Search Console
            property to finish.
          </template>
          <template v-else>Not connected. Connect to enable automated tracking</template>
        </div>
      </div>
      <span class="wg-statpill" :class="isConnected ? 'on' : 'off'">
        {{ isExpired ? 'Access expired' : isConnected ? 'Connected' : 'Not connected' }}
      </span>
    </div>

    <!-- disconnected -->
    <template v-if="!isConnected && !isExpired">
      <div class="wg-notice">
        <span class="ico"><i class="material-icons-outlined">warning</i></span>
        <div class="body">
          <div class="nt">Search Console is required for automated Wins reports</div>
          <div class="nb">
            Without Search Console, we can't automatically track your content's keyword rankings,
            impressions, and clicks. You can still add URLs manually, but automated AI mapping and
            {{ freqAdverb }} reports require a connection.
          </div>
        </div>
      </div>
      <button class="wg-pill solid-blue" @click="$emit('open-wizard')">
        Connect Google Search Console
      </button>
    </template>

    <!-- connected / expired -->
    <template v-else>
      <div v-if="isExpired" class="wg-notice">
        <span class="ico"><i class="material-icons-outlined">warning</i></span>
        <div class="body">
          <div class="nt">Google disconnected us. Reconnect to resume tracking.</div>
          <div class="nb">
            This happens when a Google password changes or access is revoked. Your tracked URLs,
            history and settings are all kept. Reconnecting takes about a minute.
          </div>
        </div>
        <button class="wg-pill solid-blue sm" @click="$emit('open-wizard')">Reconnect</button>
      </div>

      <div class="wg-gsc-grid">
        <div v-for="field in connectionFields" :key="field.k" class="wg-gsc-field">
          <span class="k">{{ field.k }}</span>
          <span class="v">{{ field.v }}</span>
        </div>
      </div>

      <div class="wg-consent">
        <label class="wg-cbx" :class="{ checked: monitoringDraft }" @click.prevent="monitoringDraft = !monitoringDraft">
          <span class="cbx-box"><i v-if="monitoringDraft" class="material-icons">check</i></span>
          <span class="lbl">Enable ongoing performance monitoring</span>
        </label>
        <div class="ctext">
          I authorize POP to access my Google Search Console data on an ongoing basis to monitor
          keyword rankings, impressions, clicks, and CTR. POP will check performance
          {{ freqAdverb }} and generate Wins reports.<br />
          We only access Search Console data, nothing else from your Google account. You can revoke
          access at any time.
        </div>
      </div>

      <div class="wg-terms">
        <label class="wg-cbx" :class="{ checked: termsDraft }" @click.prevent="toggleTerms">
          <span class="cbx-box"><i v-if="termsDraft" class="material-icons">check</i></span>
          <span class="lbl">
            I agree to the
            <a href="#" @click.prevent.stop="termsDoc = 'terms'">Performance Monitoring Terms &amp; Conditions</a>
            and
            <a href="#" @click.prevent.stop="termsDoc = 'policy'">Data Access Policy.</a>
          </span>
        </label>
      </div>
      <wg-terms-modal v-if="termsDoc" :doc="termsDoc" @close="termsDoc = null" />

      <div class="wg-gsc-btns">
        <button class="wg-pill outline sm" :disabled="saving || !dirty" @click="saveSettings">
          {{ saving ? 'Saving…' : 'Save settings' }}
        </button>
        <button class="wg-pill outline-neutral sm" @click="$emit('open-wizard')">Change GSC account</button>
        <button class="wg-pill outline-red sm" @click="showDisconnectModal = true">Disconnect GSC</button>
      </div>
    </template>

    <wg-modal
      :open="showDisconnectModal"
      title="Disconnect Search Console?"
      body="Automated tracking and Wins reports will stop until you reconnect. Existing data is kept."
      icon="link_off"
      tone="danger"
      confirm-label="Disconnect"
      confirm-tone="danger"
      :busy="disconnecting"
      busy-label="Disconnecting…"
      @confirm="disconnect"
      @close="showDisconnectModal = false"
    />
  </div>
</template>

<script>
import moment from 'moment'
import dataForSeoLocations from '@/consts/dataforseo_locations.json'
import languages from '@/consts/dataforseo_languages.json'
import { DATE_RANGE_OPTIONS } from '@/components/Campaigns/keyword-wizard/DateRangeSelect.vue'
import WgModal from '@/components/SitesSingleView/WhiteGlove/Settings/WgModal.vue'
import WgTermsModal from '@/components/SitesSingleView/WhiteGlove/Settings/WgTermsModal.vue'

export default {
  name: 'GscConnectionCard',
  components: { WgModal, WgTermsModal },
  inject: ['wgToast'],
  props: {
    /** GscConnection payload from getGscConnection, or null. */
    connection: {
      type: Object,
      default: null,
    },
    freqAdverb: {
      type: String,
      default: 'quarterly',
    },
  },
  data () {
    return {
      availableAccountEmail: '',
      monitoringDraft: false,
      termsDraft: false,
      termsDoc: null, // 'terms' | 'policy' | null
      saving: false,
      showDisconnectModal: false,
      disconnecting: false,
    }
  },
  computed: {
    isConnected () {
      return Boolean(this.connection) && this.connection.status === 'connected'
    },
    isExpired () {
      return Boolean(this.connection) && this.connection.status === 'expired'
    },
    accountName () {
      if (!this.connection) return ''
      return this.connection.googleName || this.connection.googleEmail
    },
    accountLabel () {
      if (!this.connection) return ''
      const name = this.connection.googleName
      const email = this.connection.googleEmail
      return name ? `${name} (${email})` : email
    },
    termsAccepted () {
      return Boolean(this.connection && this.connection.termsAcceptedAt)
    },
    dirty () {
      if (!this.connection) return false
      return this.monitoringDraft !== Boolean(this.connection.monitoringEnabled) ||
        (this.termsDraft && !this.termsAccepted)
    },
    connectionFields () {
      const c = this.connection || {}
      return [
        { k: 'Domain', v: c.propertyUrl || '–' },
        { k: 'Location', v: this.locationName(c.locationCode) },
        { k: 'Language', v: this.languageName(c.languageCode) },
        { k: 'Last data pull', v: this.fmtDate(c.lastPullAt) },
        { k: 'Data period', v: this.periodLabel(c.initialDatePeriod) },
        { k: 'Next scheduled pull', v: this.fmtDate(c.nextPullAt) },
      ]
    },
  },
  watch: {
    connection: {
      handler (value) {
        this.monitoringDraft = Boolean(value && value.monitoringEnabled)
        this.termsDraft = Boolean(value && value.termsAcceptedAt)
        if (!value) {
          this.checkAvailableAccount()
        }
      },
      immediate: true,
    },
  },
  methods: {
    /** An account connected elsewhere (campaigns GoogleLogin) that the wizard can reuse. */
    checkAvailableAccount () {
      this.$store.dispatch('getGoogleAccessToken')
        .then((response) => {
          const data = response && response.data
          this.availableAccountEmail =
            data && data.status === 'SUCCESS' && data.googleEmail ? data.googleEmail : ''
        })
        .catch(() => {
          this.availableAccountEmail = ''
        })
    },
    locationName (code) {
      if (!code) return '–'
      const numeric = parseInt(code, 10)
      const entry = dataForSeoLocations.locations.find((l) => l.location_code === numeric)
      return entry ? entry.location_name : String(code)
    },
    languageName (code) {
      if (!code) return '–'
      const entry = languages.find((l) => l.language_code === code)
      return entry ? entry.language_name : code
    },
    periodLabel (value) {
      const option = DATE_RANGE_OPTIONS.find((o) => o.value === value)
      return option ? option.label : (value || '–')
    },
    fmtDate (value) {
      return value ? moment(value).format('MMM D, YYYY') : '–'
    },
    toggleTerms () {
      // consent can be given here, but not withdrawn once recorded
      if (this.termsAccepted) return
      this.termsDraft = !this.termsDraft
    },
    saveSettings () {
      if (!this.dirty || this.saving) return
      if (this.monitoringDraft && !this.termsDraft) {
        this.wgToast('The terms must be accepted to enable monitoring', null, 'error')
        return
      }
      this.saving = true
      const body = { monitoringEnabled: this.monitoringDraft ? 1 : 0 }
      if (this.termsDraft && !this.termsAccepted) {
        body.termsAccepted = 1
      }
      this.$store.dispatch('updateGscSettings', {
        siteId: this.connection.siteId,
        body,
      })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.wgToast('GSC settings saved')
            this.$emit('updated', response.data)
          } else {
            const msg = (response.data && response.data.msg) || 'Could not save GSC settings'
            this.wgToast(msg, null, 'error')
          }
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.msg) ||
            'Could not save GSC settings'
          this.wgToast(msg, null, 'error')
        })
        .finally(() => {
          this.saving = false
        })
    },
    disconnect () {
      if (this.disconnecting) return
      this.disconnecting = true
      this.$store.dispatch('disconnectGsc', this.connection.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.showDisconnectModal = false
            this.wgToast('Search Console disconnected')
            this.$emit('updated', response.data)
          } else {
            this.wgToast('Could not disconnect', null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not disconnect', null, 'error')
        })
        .finally(() => {
          this.disconnecting = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

/* GSC panel head */
.wg-gsc-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  .txt {
    flex: 1;
    min-width: 0;

    .t {
      font-size: 1.125rem;
      font-weight: 700;
      color: $pop-teal;
    }

    .s {
      font-size: 0.9rem;
      color: $pop-ink-muted;
      margin-top: 3px;
    }
  }
}

.wg-gico,
.wg-gico-blue {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.wg-gico {
  background: $pop-ink-faint;
}

.wg-gico-blue {
  background: $pop-link;
}

.wg-gsc-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 20px 0;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
}

.wg-gsc-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: $wg-lavender;
  border: 1px solid $wg-panel-line;
  border-radius: 8px;
  padding: 12px 16px;

  .k {
    font-size: 0.85rem;
    color: $pop-ink-muted;
    white-space: nowrap;
  }

  .v {
    font-size: 0.85rem;
    color: $pop-teal;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.wg-gsc-btns {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
</style>
